/**
 * Copy that a code alone cannot pick, because the same code means something
 * different depending on the call site.
 *
 * `CONFLICT` is thrown from many endpoints across many services, and only the
 * provider sign-in verify endpoints mean "an account already uses that email
 * address". Everywhere else `CONFLICT` keeps its generic class sentence from
 * `errorClassCopy.conflict`. So the sentence below cannot live keyed on the
 * code alone; it is keyed on a context tag the call site opts into, and only
 * that call site is allowed to pass the tag. See ISSUE-453 and AUTH-2.
 */

/**
 * A context is a TAG, never a sentence. Add a new member here, and only here,
 * when a future call site needs the same kind of code-means-something-else
 * disambiguation.
 */
export type ErrorCopyContext =
  | "providerSignIn"
  | "signInMethodRemove"
  | "gifSelect"
  | "photoUpload"
  | "accountState"
  | "contestRulesEdit"
  | "entryCreate"
  | "poolCreate"
  | "chatCreate"
  | "squares";

export const PROVIDER_SIGN_IN_EMAIL_TAKEN_COPY =
  "An account already uses that email address. Sign in with your email and we'll send you a sign-in code.";

/**
 * ISSUE-115. The sign-in-methods screen does not offer Remove on a fan's only
 * linked method, so this is reached only by a stale tab: two methods in the
 * cached list, one of them already gone. The generic conflict sentence would
 * tell that fan someone beat them to it, which is not what happened.
 *
 * The same sentence the screen renders in place of the Remove action, per
 * design prompt 44.1. Deliberately not a second wording for one rule.
 */
export const LAST_SIGN_IN_METHOD_COPY = "Keep at least one way to sign in.";

/**
 * ISSUE-778. `GIF_RATING_NOT_ALLOWED` classes as `forbidden`, whose class
 * sentence ("You don't have access to do that") reads as a permissions
 * problem. Nothing is locked and no permission is missing; the fan picked a
 * GIF above `messaging.gifContentRatingCeiling`. This sentence names the
 * actual reason and the one next step, per `copy-style.md`'s error-copy rule.
 * The chat composer's GIF picker (spec section 39.6, MSG-6/Q83, MSG-23/Q151)
 * is the intended caller, through `showApiError(result, "gifSelect")`.
 */
export const GIF_SELECT_RATING_NOT_ALLOWED_COPY =
  "That GIF is too explicit. Pick a different one.";

/**
 * ISSUE-839. `FILE_TOO_LARGE` classes as `validation`, whose class sentence
 * ("That doesn't look right. Check the info and try again.") tells a fan their
 * input was wrong. Their input was fine. It was over media-api's
 * 15,000,000-byte ceiling. This sentence says the file was too big and gives
 * the one next step.
 *
 * One sentence covers a GIF, a chat photo, an avatar and an admin mark, so it
 * says "file" rather than naming one of them. Two sentences for one refusal is
 * what this constant exists to prevent: `apps/admin` used to write its own at
 * the call site, and nothing else could reuse it.
 *
 * It names no size on purpose. The refusal carries no byte number: all five
 * raise sites in media-api send `details` of `{ ref }` plus, on the GIF path,
 * `{ reason: "gifOverByteCap" }`, and the generated envelope types `details` as
 * `Record<string, never>`. The ceiling exists only as a repeated `15_000_000`
 * literal in six files with no shared constant, so a number written here would
 * be a seventh copy that nothing checks. An inaccurate number is worse than
 * none.
 */
export const FILE_TOO_LARGE_COPY = "That file is too big. Pick a smaller one.";

/**
 * ISSUE-790. A suspended account refused at sign-in. The forbidden class
 * sentence "You don't have access to do that." names neither the state nor a
 * way back, so a stopped fan would not know what happened or what to do next.
 * The appeal address in the sentence is the one the owner named on
 * 2026-09-14.
 */
export const ACCOUNT_SUSPENDED_COPY =
  "Your account is suspended. Email support@sportsmet.com and we'll take another look.";

/**
 * ISSUE-790. A banned account refused at sign-in. The forbidden class
 * sentence "You don't have access to do that." names neither the state nor a
 * way back. The appeal address in the sentence is the one the owner named on
 * 2026-09-14.
 */
export const ACCOUNT_BANNED_COPY =
  "Your account is banned. Email support@sportsmet.com if you think this is a mistake.";

/**
 * ISSUE-1041. The six refusals of /contest/update, each with its own sentence
 * in the Change contest rules dialog. CONFLICT from /contest/update only ever
 * means contestStarted: a date change once the contest has a round.
 */
export const CONTEST_RULES_EDIT_VALIDATION_COPY =
  "These rules can't be saved as they are. Check each number, and pick a start date of today or later.";
export const CONTEST_RULES_EDIT_STARTED_COPY =
  "This contest has started, so its dates can't change anymore. We put the dates back. Save again to keep your other changes.";
export const CONTEST_RULES_EDIT_OVERLAP_COPY =
  "Those dates overlap another contest in this pool. Pick dates that don't.";
export const CONTEST_RULES_EDIT_ENDED_COPY =
  "This contest has ended, so its rules can't change anymore.";
export const CONTEST_RULES_EDIT_FORBIDDEN_COPY =
  "Only the Commissioner and Admins can change this pool's rules.";
export const CONTEST_RULES_EDIT_NOT_FOUND_COPY =
  "We couldn't find this contest. It may have been removed.";

/**
 * ISSUE-1043. /entry/create refuses with MAX_ENTRIES_REACHED once the member
 * holds as many entries as the contest allows. The conflict class sentence
 * "That's already been done, or someone beat you to it." reads as a race, not
 * as a cap, so the fan would not know why nothing happened.
 */
export const ENTRY_CREATE_MAX_ENTRIES_COPY =
  "You already have the most entries this contest allows.";

/**
 * ISSUE-1043. /entry/create refuses with FORBIDDEN_POOL. The forbidden class
 * sentence reads as a permissions problem. The handler raises FORBIDDEN_POOL
 * for three causes (the caller is not an active member, the pool-api
 * membership call failed, or the caller is outside the first-round join
 * boundary: they joined at or after that round's lock, or the round has no
 * lock instant) and the envelope does not say which, so this sentence names
 * no cause and must stay true for all three.
 */
export const ENTRY_CREATE_FORBIDDEN_COPY =
  "You can't add an entry to this contest right now.";

/**
 * ISSUE-1043. /entry/create refuses with CONTEST_ALREADY_ENDED once the
 * contest is confirmed. The conflict class sentence reads as a race, while
 * the real reason is that the contest is over.
 */
export const ENTRY_CREATE_CONTEST_ENDED_COPY =
  "This contest is over, so you can't add an entry.";

/**
 * ISSUE-1105. /entry/create refuses with ENTRY_WINDOW_CLOSED once the
 * contest's first round has locked (SUR-R39, owner answer Q-1043-A). The
 * conflict class sentence reads as a race, while the real reason is that
 * the time to add an entry has passed.
 */
export const ENTRY_CREATE_WINDOW_CLOSED_COPY =
  "The first round has started, so you can't add another entry.";

/**
 * ISSUE-1302. /pool/create answers CONTEST_TYPE_NOT_READY when contest-api
 * cannot start the chosen pool type yet: a Pick'em pool whose league has not
 * published its season schedule. The conflict class sentence reads as a race.
 * The real reason is the schedule, and the next step is to wait for it. The
 * half-made pool is already archived, so nothing is left to clean up.
 */
export const POOL_CREATE_NOT_READY_COPY =
  "This league hasn't posted its season schedule yet, so we couldn't start your pool. Try again once the games are out.";

/**
 * ISSUE-1368. /chat/create-standalone and /chat/get-or-create-private refuse
 * with CHAT_SUSPENDED while staff have chat switched off for the account. The
 * forbidden class sentence reads as a permissions problem with no reason and
 * no way forward. This one names who switched it off and what still works,
 * in the words of the line that replaces the composer
 * (CHAT_SUSPENDED_COMPOSER_LINE in both apps' memberTimeoutLogic.ts).
 */
export const CHAT_CREATE_SUSPENDED_COPY =
  "Our staff turned off posting for your account, so you can't start a new chat. You can still read chats and react.";

/**
 * ISSUE-1262 QA. A Squares refusal that classes as `conflict` read "That's
 * already been done, or someone beat you to it." for a per-member limit, a
 * frozen setting and a season still in play. Each sentence names the real
 * reason and the next step, in the words the mobile app already uses
 * (apps/mobile/src/squares/squaresErrors.ts).
 */
export const SQUARES_MAX_SQUARES_COPY =
  "That is the most squares allowed on this grid. Give one back before taking another.";
export const SQUARES_SETTING_FROZEN_COPY =
  "Grid size, axes and winning squares stay the same for the season once the first grid locks.";
export const SQUARES_SEASON_NOT_FINISHED_COPY =
  "A new season can start once every published grid is final.";
export const SQUARES_GRID_STARTED_COPY =
  "This game has started or a winner is set, so that can't change anymore.";

/**
 * QA round 1 (ISSUE-1427). SQUARE_TAKEN is a real race, so the conflict class
 * sentence is not wrong, but it does not say what to do next. The board
 * refetches on this refusal, so the next step is simply another square.
 */
export const SQUARES_SQUARE_TAKEN_COPY =
  "Someone just took that square. The board is up to date, so pick another one.";

/**
 * QA round 3 (ISSUE-1263, ISSUE-1270). A Squares `CONFLICT` means a grid is
 * in the wrong publish or lock state for the action, and its sentence depends
 * on the refusal's machine reason (`details.reason`), so these are keyed on
 * `context + code + reason`. `notLocked` comes from Add a winner on an open
 * grid (its numbers are not drawn yet) and from Unlock on a grid that is
 * already open; one sentence stays true for both.
 */
export const SQUARES_GRID_NOT_LOCKED_COPY =
  "This grid is still open, so its numbers aren't drawn yet. Lock it first.";
export const SQUARES_GRID_DRAFT_COPY =
  "This grid is still a draft. Publish it first.";
export const SQUARES_GRID_ALREADY_PUBLISHED_COPY =
  "This grid is already published.";
export const SQUARES_GRID_NOT_PUBLISHED_COPY = "This grid is already a draft.";

const SQUARES_CONFLICT_REASON_COPY: ReadonlyMap<string, string> = new Map([
  ["notLocked", SQUARES_GRID_NOT_LOCKED_COPY],
  ["draft", SQUARES_GRID_DRAFT_COPY],
  ["alreadyPublished", SQUARES_GRID_ALREADY_PUBLISHED_COPY],
  ["notPublished", SQUARES_GRID_NOT_PUBLISHED_COPY],
]);

/**
 * Resolves a `context + code` pair to its sentence, or `null` when the pair
 * has none. `reason` is the refusal's optional machine reason
 * (`details.reason`); only a context that documents reason-keyed copy reads
 * it, so a caller that has no reason gets the same answer as before. `null` means the caller falls back to its own class-copy
 * resolver; this function never falls back itself, because `apps/app` and
 * `apps/admin` have different unknown-code fallback sentences and neither
 * belongs in this package.
 */
export function resolveContextCopy({
  context,
  code,
  reason,
}: {
  context: ErrorCopyContext;
  code: string;
  reason?: string | null | undefined;
}): string | null {
  // The sentence may say an account already uses the email address and may
  // point at the one-time-code path. It must never name the account, the
  // username, the linked providers, or echo the email address back. Same
  // "one signal, not two" discipline the avatar/set refusal enforces.
  if (context === "providerSignIn" && code === "CONFLICT")
    return PROVIDER_SIGN_IN_EMAIL_TAKEN_COPY;
  if (context === "signInMethodRemove" && code === "LAST_SIGN_IN_METHOD")
    return LAST_SIGN_IN_METHOD_COPY;
  if (context === "gifSelect" && code === "GIF_RATING_NOT_ALLOWED")
    return GIF_SELECT_RATING_NOT_ALLOWED_COPY;
  // Both contexts answer with the SAME constant. A GIF picker and a photo
  // upload are two ways to hit one refusal, and one refusal gets one sentence.
  if (context === "gifSelect" && code === "FILE_TOO_LARGE")
    return FILE_TOO_LARGE_COPY;
  if (context === "photoUpload" && code === "FILE_TOO_LARGE")
    return FILE_TOO_LARGE_COPY;
  // A stopped account (ISSUE-790). Two contexts, one sentence per state.
  if (context === "accountState" && code === "ACCOUNT_SUSPENDED")
    return ACCOUNT_SUSPENDED_COPY;
  if (context === "accountState" && code === "ACCOUNT_BANNED")
    return ACCOUNT_BANNED_COPY;
  if (context === "providerSignIn" && code === "ACCOUNT_SUSPENDED")
    return ACCOUNT_SUSPENDED_COPY;
  if (context === "providerSignIn" && code === "ACCOUNT_BANNED")
    return ACCOUNT_BANNED_COPY;
  if (context === "contestRulesEdit" && code === "VALIDATION_ERROR")
    return CONTEST_RULES_EDIT_VALIDATION_COPY;
  if (context === "contestRulesEdit" && code === "CONFLICT")
    return CONTEST_RULES_EDIT_STARTED_COPY;
  if (context === "contestRulesEdit" && code === "CONTEST_DATES_OVERLAP")
    return CONTEST_RULES_EDIT_OVERLAP_COPY;
  if (context === "contestRulesEdit" && code === "CONTEST_ALREADY_ENDED")
    return CONTEST_RULES_EDIT_ENDED_COPY;
  if (context === "contestRulesEdit" && code === "FORBIDDEN_ROLE")
    return CONTEST_RULES_EDIT_FORBIDDEN_COPY;
  if (context === "contestRulesEdit" && code === "NOT_FOUND")
    return CONTEST_RULES_EDIT_NOT_FOUND_COPY;
  // ISSUE-1043. Starting another entry in a contest.
  if (context === "entryCreate" && code === "MAX_ENTRIES_REACHED")
    return ENTRY_CREATE_MAX_ENTRIES_COPY;
  if (context === "entryCreate" && code === "FORBIDDEN_POOL")
    return ENTRY_CREATE_FORBIDDEN_COPY;
  if (context === "entryCreate" && code === "CONTEST_ALREADY_ENDED")
    return ENTRY_CREATE_CONTEST_ENDED_COPY;
  if (context === "entryCreate" && code === "ENTRY_WINDOW_CLOSED")
    return ENTRY_CREATE_WINDOW_CLOSED_COPY;
  if (context === "poolCreate" && code === "CONTEST_TYPE_NOT_READY")
    return POOL_CREATE_NOT_READY_COPY;
  // ISSUE-1368. Starting a stand-alone chat or a private chat.
  if (context === "chatCreate" && code === "CHAT_SUSPENDED")
    return CHAT_CREATE_SUSPENDED_COPY;
  // ISSUE-1262 QA. Every Squares mutation on web.
  if (context === "squares" && code === "MAX_SQUARES_REACHED")
    return SQUARES_MAX_SQUARES_COPY;
  if (context === "squares" && code === "SQUARES_SETTING_FROZEN")
    return SQUARES_SETTING_FROZEN_COPY;
  if (context === "squares" && code === "SEASON_NOT_FINISHED")
    return SQUARES_SEASON_NOT_FINISHED_COPY;
  if (context === "squares" && code === "GRID_STARTED")
    return SQUARES_GRID_STARTED_COPY;
  // QA round 1 (ISSUE-1427). A real race still says what to do next.
  if (context === "squares" && code === "SQUARE_TAKEN")
    return SQUARES_SQUARE_TAKEN_COPY;
  // QA round 3. A grid in the wrong state names that state, by reason.
  if (
    context === "squares" &&
    code === "CONFLICT" &&
    typeof reason === "string"
  )
    return SQUARES_CONFLICT_REASON_COPY.get(reason) ?? null;
  return null;
}
